import React, { useEffect } from "react";
import PatientSidebar from "../PatientSidebar";
import DraggableSection from "./DraggableSection";
import useTableCatalogItems from "../../../context/catalog/useTableCatalogItems";
import { useCatalog } from "../../../api/hooks/useCatalog";
import { usePatientAuthFlags } from "../../../Hooks/usePatientAuthFlags";
import { MIC_TOGGLE_TARGET } from "../utils/micStartTarget";
/**
 * Component of the sidebar section
 *
 * @param {Object} props
 * @param {string} props.id - Identifier for DnD
 * @param {string} props.language - Current UI language code
 * @param {Function} props.onMacroSelect - Handler for selecting a macro
 * @param {Function} props.onBeforeSidebarMicStart - Stops other active mic before sidebar starts
 * @param {Function} props.registerSidebarStopMicHandler - Registers sidebar stop handler in parent
 * @param {Function} props.registerSidebarStartMicHandler - Registers sidebar start handler in parent
 * @param {Function} props.onSidebarFocus - Focus handler for dictation textarea (mic orchestration)
 * @param {Function} props.onSidebarRecordingChange - Reports sidebar recording state to parent
 * @param {Function} props.onSidebarMicClick - Manual sidebar mic toggle from parent
 * @param {boolean} props.isAnyMicActive - Any mic recording (unified sidebar button)
 * @param {Function} props.onMacrosReady - Callback when macro list is loaded/updated (for voice macro commands)
 * @param {Function} props.onTemplatesReady - Callback when template list is loaded/updated (for voice template commands)
 * @param {Function} props.onTemplateSelect - Handler for selecting a template
 * @param {string|null} props.selectedTemplateId - Currently selected template id
 * @param {Function} props.onSelectedTemplateIdChange - Setter for selected template id in parent
 * @param {string} props.relatedUserId - ID of the related user
 * @param {string} props.sidebarTranscript - Transcript from the sidebar's voice input
 * @param {Function} props.setSidebarTranscript - Function to update sidebar transcript
 * @param {string} props.anathomy - Selected anathomy code
 * @param {string} props.dictationLanguage - Current dictation language
 * @param {Function} props.setDictationLanguage - Function to update dictation language
 * @param {Array} props.anathomies - Array of anatomy objects with id, code, and name
 * @param {Function} props.onProcessWithAI - Runs Process with AI (same as report editor toolbar)
 * @param {boolean} props.isAiLoading - True while Process with AI is running (CRE PiP shows busy state)
 * @param {Function} [props.onVoiceMacroResult] - Same shape as editor: macro voice command feedback (toasts)
 * @returns {JSX.Element}
 */
const SidebarSection = ({
  id,
  language,
  onBeforeSidebarMicStart,
  registerSidebarStopMicHandler,
  registerSidebarStartMicHandler,
  registerSidebarClearReportHandler,
  onDeleteTranscriptionRequest,
  findingsTextareaRef,
  micToggleTarget = MIC_TOGGLE_TARGET.FINDINGS,
  onSidebarFocus,
  onSidebarRecordingChange,
  onSidebarMicClick,
  isAnyMicActive = false,
  onMacroSelect,
  onMacrosReady,
  onTemplatesReady,
  onTemplateSelect,
  onTemplateAppend,
  selectedTemplateId,
  onSelectedTemplateIdChange,
  relatedUserId,
  sidebarTranscript,
  setSidebarTranscript,
  anathomy,
  dictationLanguage,
  setDictationLanguage,
  anathomies = [],
  onProcessWithAI,
  isAiLoading = false,
  onVoiceMacroResult,
  modelSpeed,
  setModelSpeed,
  findingsOnly,
  onFindingsOnlyChange,
}) => {
  const { setTableCatalogItems } = useTableCatalogItems();
  const { getCatalog } = useCatalog();
  const { isRadicalAuth } = usePatientAuthFlags();

  useEffect(() => {
    const fetchTableCatalogItems = async () => {
      const tableResponseAPI = await getCatalog({ catalogType: 4 });
      setTableCatalogItems(tableResponseAPI.catalogItems);
    };
    fetchTableCatalogItems();
  }, [getCatalog, setTableCatalogItems]);

  return (
    <DraggableSection id={id} className="lg:w-2/6 w-full">
      {() => (
        <div
          className={`w-full min-h-0 flex flex-col ${
            isRadicalAuth ? "lg:min-h-[calc(100vh-8rem)]" : "lg:min-h-[800px]"
          }`}
        >
          <PatientSidebar
            language={language}
            onBeforeStartRecording={onBeforeSidebarMicStart}
            registerStopMicHandler={registerSidebarStopMicHandler}
            registerStartMicHandler={registerSidebarStartMicHandler}
            registerClearReportHandler={registerSidebarClearReportHandler}
            onDeleteTranscriptionRequest={onDeleteTranscriptionRequest}
            findingsTextareaRef={findingsTextareaRef}
            micToggleTarget={micToggleTarget}
            onSidebarFocus={onSidebarFocus}
            onSidebarRecordingChange={onSidebarRecordingChange}
            onSidebarMicClick={onSidebarMicClick}
            isAnyMicActive={isAnyMicActive}
            onMacroSelect={onMacroSelect}
            onMacrosReady={onMacrosReady}
            onTemplatesReady={onTemplatesReady}
            onTemplateSelect={onTemplateSelect}
            onTemplateAppend={onTemplateAppend}
            selectedTemplateId={selectedTemplateId}
            onSelectedTemplateIdChange={onSelectedTemplateIdChange}
            relatedUserId={relatedUserId}
            sidebarTranscript={sidebarTranscript}
            setSidebarTranscript={setSidebarTranscript}
            anathomy={anathomy}
            dictationLanguage={dictationLanguage}
            setDictationLanguage={setDictationLanguage}
            anathomies={anathomies}
            onProcessWithAI={onProcessWithAI}
            isAiLoading={isAiLoading}
            onVoiceMacroResult={onVoiceMacroResult}
            modelSpeed={modelSpeed}
            setModelSpeed={setModelSpeed}
            findingsOnly={findingsOnly}
            onFindingsOnlyChange={onFindingsOnlyChange}
          />
        </div>
      )}
    </DraggableSection>
  );
};

export default SidebarSection;
